import { useState } from 'react';
import { connect } from 'react-redux';
import {
  IconCheck2,
  IconSplitCard,
  IconWireTransfer,
  IconZelleTransfer,
} from '@belong/icons';
import { ButtonBase } from '@belong/ui';
import cx from 'classnames';
import { find, filter } from 'es-toolkit/compat';
import PropTypes from 'prop-types';
import DottedMenu from '../../components/DottedMenu/DottedMenu';
import GeneralIcon, {
  GENERAL_ICONS,
} from '../../components/GeneralIcon/GeneralIcon';
import PaymentMethodDisplay from '../../components/Payments/PaymentMethodDisplay/PaymentMethodDisplay';
import { MODALS } from '../../containercomponents/Modals/modal.consts';
import FullUser from '../../models/composed/FullUser';
import { PaymentMethod, PaymentPreference } from '../../models/enums';
import { showModal, hideModal } from '../../store/redux/modals/actions';
import { selectFullUser } from '../../store/redux/user/selectors';

const PAYMENT_MENU_ACTIONS = {
  ADD_PAYMENT: 'add_payment',
  AUTO_SPLIT: 'auto_split',
  AUTO_SPLIT_MODAL: 'auto_split_modal',
  WIRE_TRANSFER: 'wire_transfer',
  ZELLE_TRANSFER: 'zelle_transfer',
  VERIFY: 'verify',
};

function PaymentMenuSelectionContainer(props) {
  const [showMenu, setShowMenu] = useState(true);
  const {
    hasNoPaymentMethod,
    fullUser,
    forcePaymentMethod,
    onSelectPaymentMethod,
    ...rest
  } = props;

  const handleMenuItemClick = (key) => {
    const method = getAvailablePaymentMethods().find(
      (pm) => pm.accountId === key,
    );
    if (method?.jpmAchSetup) {
      props.onSelectPendingPaymentMethod?.(method);
      return;
    }
    onSelectPaymentMethod(key);
  };

  const handleSetUserAutoSplit = () => {
    const { onSelectAutoSplitPaymentMethod } = props;
    onSelectAutoSplitPaymentMethod();
  };

  const handleAddPaymentMethod = () => {
    const {
      showModal: showModalAction,
      paymentModalProps,
      shouldShowAutoSplitPaymentMethod,
    } = props;

    showModalAction(MODALS.PAYMENT_MODAL, {
      ...paymentModalProps,
      onPaymentSelection: onSelectPaymentMethod,
      fullUser,
      onAutoSplitPaymentMethodSelect: handleSetUserAutoSplit,
      shouldShowAutoSplitPaymentMethod,
    });
  };

  const getMenuActions = () => {
    const { canVerifyPaymentMethod, onVerifyPaymentMethod } = props;
    const actions = [
      {
        name: 'ADD PAYMENT METHOD',
        key: PAYMENT_MENU_ACTIONS.ADD_PAYMENT,
        icon: <GeneralIcon icon={GENERAL_ICONS.ADD_CARD.DEFAULT} />,
        iconSelected: <GeneralIcon icon={GENERAL_ICONS.ADD_CARD.INVERSE} />,
      },
    ];

    if (canVerifyPaymentMethod && onVerifyPaymentMethod) {
      const verifiableMethod = getAvailablePaymentMethods().find(
        canVerifyPaymentMethod,
      );
      if (verifiableMethod) {
        actions.unshift({
          name: 'VERIFY',
          key: PAYMENT_MENU_ACTIONS.VERIFY,
          icon: <IconCheck2 width={16} height={16} />,
          iconSelected: <IconCheck2 width={16} height={16} />,
        });
      }
    }

    return actions;
  };

  const handleInfoModal = () => {
    const { showModal: showModalAction } = props;
    setShowMenu(false);
    showModalAction(MODALS.SPLIT_IT_LEARN_MORE, {
      setShowMenu,
    });
  };

  const handleMenuActionClick = (key) => {
    if (key === PAYMENT_MENU_ACTIONS.AUTO_SPLIT_MODAL) {
      handleInfoModal();
    } else if (key === PAYMENT_MENU_ACTIONS.ADD_PAYMENT) {
      handleAddPaymentMethod();
    } else if (key === PAYMENT_MENU_ACTIONS.AUTO_SPLIT) {
      handleSetUserAutoSplit();
    } else if (key === PAYMENT_MENU_ACTIONS.WIRE_TRANSFER) {
      props.onSelectWireTransferPaymentMethod?.();
    } else if (key === PAYMENT_MENU_ACTIONS.ZELLE_TRANSFER) {
      props.onSelectZelleTransferPaymentMethod?.();
    } else if (key === PAYMENT_MENU_ACTIONS.VERIFY) {
      const { canVerifyPaymentMethod, onVerifyPaymentMethod } = props;
      if (canVerifyPaymentMethod && onVerifyPaymentMethod) {
        const verifiableMethod = getAvailablePaymentMethods().find(
          canVerifyPaymentMethod,
        );
        if (verifiableMethod) onVerifyPaymentMethod(verifiableMethod);
      }
    }
  };

  const getTopMenuActions = () => {
    const { shouldShowAutoSplitPaymentMethod } = props;
    const actions = [];

    if (shouldShowAutoSplitPaymentMethod) {
      actions.push({
        name: 'SPLIT IT',
        key: PAYMENT_MENU_ACTIONS.AUTO_SPLIT,
        text: 'Interest Applied • Deducted From Rent',
        icon: <IconSplitCard width={46} height={30} />,
        children: (
          <ButtonBase
            className={cx('mt-sm', 'text-green hover:opacity-75 z-10')}
            onClick={(e) => {
              handleMenuActionClick(PAYMENT_MENU_ACTIONS.AUTO_SPLIT_MODAL);
              e.stopPropagation();
            }}
          >
            HOW IT WORKS
          </ButtonBase>
        ),
      });
    }

    return actions;
  };

  const getBottomMenuActions = () => {
    const {
      shouldShowWireTransferPaymentMethod,
      shouldShowZelleTransferPaymentMethod,
    } = props;
    const actions = [];

    if (shouldShowWireTransferPaymentMethod) {
      actions.push({
        name: 'WIRE TRANSFER',
        key: PAYMENT_MENU_ACTIONS.WIRE_TRANSFER,
        paymentMethodRow: true,
        icon: <IconWireTransfer width={46} height={30} />,
      });
    }

    if (shouldShowZelleTransferPaymentMethod) {
      actions.push({
        name: 'ZELLE TRANSFER',
        key: PAYMENT_MENU_ACTIONS.ZELLE_TRANSFER,
        paymentMethodRow: true,
        icon: (
          <IconZelleTransfer
            aria-hidden="true"
            width={46}
            height={30}
            className="h-[30px] w-[46px] shrink-0"
          />
        ),
      });
    }

    return actions;
  };

  const getAvailablePaymentMethods = () => {
    const {
      extraPaymentMethods,
      ignorePaymentMethodId,
      showOnlyValidPaymentMethods,
    } = props || {};

    const paymentMethods = filter(
      fullUser.getPaymentMethods(
        forcePaymentMethod,
        showOnlyValidPaymentMethods,
      ),
      (paymentMethod) => paymentMethod?.accountId !== ignorePaymentMethodId,
    );
    const extra = filter(
      extraPaymentMethods,
      (paymentMethod) =>
        paymentMethod?.accountId !== ignorePaymentMethodId &&
        (!forcePaymentMethod ||
          paymentMethod?.paymentMethod === forcePaymentMethod) &&
        !find(paymentMethods, { accountId: paymentMethod?.accountId }),
    );

    return [...paymentMethods, ...extra];
  };

  const getMenuItems = () => {
    const { selectedPaymentMethodId, paymentType } = props || {};

    return getAvailablePaymentMethods()?.map((paymentMethod) => {
      const isCurrentPaymentMethod =
        paymentMethod?.accountId === selectedPaymentMethodId;

      return {
        name: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ width: 'calc(100% - 2rem)' }}>
              <PaymentMethodDisplay
                hasFee={!(paymentType === PaymentPreference.Payout)}
                paymentMethod={paymentMethod}
                paymentMethodType={paymentMethod?.getAccountType()}
              />
            </div>
            {isCurrentPaymentMethod && <IconCheck2 width={16} height={16} />}
          </div>
        ),
        paymentMethod,
        key: paymentMethod?.accountId,
      };
    });
  };

  return (
    <DottedMenu
      hasNoPaymentMethod={hasNoPaymentMethod}
      menuItems={getMenuItems()}
      onMenuClick={handleMenuItemClick}
      menuActions={getMenuActions()}
      topMenuActions={getTopMenuActions()}
      bottomMenuActions={getBottomMenuActions()}
      onMenuActionClick={handleMenuActionClick}
      onTop
      manuallyShowMenu={showMenu}
      {...rest}
    />
  );
}

PaymentMenuSelectionContainer.propTypes = {
  fullUser: PropTypes.instanceOf(FullUser).isRequired,
  forcePaymentMethod: PropTypes.oneOf(Object.values(PaymentMethod)),
  selectedPaymentMethodId: PropTypes.string.isRequired,
  onSelectPaymentMethod: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  hasNoPaymentMethod: PropTypes.bool,
  paymentType: PropTypes.oneOf(Object.values(PaymentPreference)).isRequired,
  showOnlyValidPaymentMethods: PropTypes.bool,
  paymentModalProps: PropTypes.object.isRequired,
  ignorePaymentMethodId: PropTypes.bool.isRequired,
  onSelectAutoSplitPaymentMethod: PropTypes.func.isRequired,
  shouldShowAutoSplitPaymentMethod: PropTypes.bool,
  onSelectWireTransferPaymentMethod: PropTypes.func,
  shouldShowWireTransferPaymentMethod: PropTypes.bool,
  shouldShowZelleTransferPaymentMethod: PropTypes.bool,
  onSelectZelleTransferPaymentMethod: PropTypes.func,
  isAutoSplitEnabled: PropTypes.bool,
  hideModal: PropTypes.func,
  extraPaymentMethods: PropTypes.array,
  onVerifyPaymentMethod: PropTypes.func,
  canVerifyPaymentMethod: PropTypes.func,
  onSelectPendingPaymentMethod: PropTypes.func,
};

PaymentMenuSelectionContainer.defaultProps = {
  forcePaymentMethod: null,
  showOnlyValidPaymentMethods: false,
  hasNoPaymentMethod: false,
  extraPaymentMethods: [],
  onVerifyPaymentMethod: null,
  canVerifyPaymentMethod: null,
  onSelectPendingPaymentMethod: null,
  onSelectWireTransferPaymentMethod: null,
  shouldShowWireTransferPaymentMethod: false,
  shouldShowZelleTransferPaymentMethod: false,
  onSelectZelleTransferPaymentMethod: null,
};

export default connect(
  (state) => ({
    fullUser: selectFullUser(state),
  }),
  {
    showModal,
    hideModal,
  },
)(PaymentMenuSelectionContainer);
